import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, History } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { HistoryContent } from "@/components/audit/HistoryContent"
import type { WorkerNameResolvers } from "@/lib/adapters/audit-history-adapter"
import { createEngagementAuditConfig, type EngagementAuditLookups } from "@/lib/audit/engagement-audit-config"
import { useFormatters } from "@/hooks/use-formatters"
import { useAuditHistory } from "@/hooks/use-audit-history"
import { useFieldMetadata } from "@/hooks/use-field-metadata"
import { useAuditLookups } from "@/hooks/use-audit-lookups"
import { useLanguage } from "@/contexts/language-context"
import type { EngagementResponse } from "@/types/api/engagement"
import { getActionLabel, type EngagementActionCode } from "@/lib/constants/engagement-actions"
import { ENGAGEMENT_LOOKUP_CATEGORIES } from "@/lib/constants/engagement-codes"

interface HistoryModalProps {
  recordId: number
  engagementName: string
  currentVersion: number // The version currently being viewed in the form
  allVersions: EngagementResponse[] | null // All current (non-deleted) versions
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Full-screen modal for displaying audit history
 * Uses Dialog primitive but customized to be full-screen
 * Creates engagement audit config internally
 */
export function HistoryModal({ recordId, engagementName, currentVersion, allVersions, open, onOpenChange }: HistoryModalProps) {
  // Get language dictionary for i18n
  const { dictionary } = useLanguage()

  // Only fetch when modal is open. The history payload carries server-resolved profile/org names
  // (references) so the diff view renders names without RLS-gated lookups for a restricted caller.
  const { auditHistory, references, loading: auditHistoryLoading, error: auditHistoryError } = useAuditHistory({ recordId, enabled: open })

  // Get field metadata (only when modal is open)
  const { getMetadata: getFieldMetadata, loading: metadataLoading, error: metadataError } = useFieldMetadata(open)

  // Resolve display values: org/profile names from the server-provided references; code-table
  // values from the global lookup caches.
  const { getOrgName, getProfileName, getAuditByName, getValue, loading: lookupsLoading } = useAuditLookups(references)

  // Get formatters
  const { formatCurrency, formatDate, formatTimestamp, formatDecimal } = useFormatters()

  // Extract valid entity IDs from current versions (excludes deleted versions)
  const validEntityIds = React.useMemo(() => {
    if (!allVersions) return undefined
    return allVersions
      .map(v => v.data.id)
      .filter((id): id is string => id !== null && id !== undefined)
  }, [allVersions])

  // Resolve "Worker #N" history headers to the worker's name (GW-310). The adapter prefers the
  // worker's own WorkerUserProfileId when the audited document records it (adds/removals) —
  // position-independent, always correct. For pure edits it uses `byAuditPosition`, which looks up
  // the worker at the audited array index in the server-reconstructed identity map for THAT audit
  // document (references.auditWorkers) — i.e. the worker order as it stood at that exact save, so a
  // crowded edit (remove one worker, edit another in the same save) still names the edited worker.
  // Both map the profile id to a name via the history references; an unresolved lookup leaves a bare
  // "Worker" rather than risk a wrong name in the audit trail. See WorkerNameResolvers.
  const resolveWorkerName = React.useMemo<WorkerNameResolvers>(() => {
    const auditWorkers = references.auditWorkers ?? {}
    const nameFor = (profileId: number | null | undefined): string | undefined =>
      profileId === null || profileId === undefined ? undefined : references.profiles[profileId]?.fullName || undefined
    const collectionKey = (collection: "Workers" | "RemovedWorkers") =>
      collection === "Workers" ? "workers" : "removedWorkers"
    return {
      byProfileId: (profileId) => nameFor(profileId),
      byAuditPosition: (auditDocId, collection, index) =>
        nameFor(auditWorkers[auditDocId]?.[collectionKey(collection)]?.[index]),
    }
  }, [references])

  // Create engagement audit config with lookups
  const auditConfig = React.useMemo(() => {
    const lookups: EngagementAuditLookups = {
      getOrgName,
      getProfileName,
      getFieldMetadata,
      formatCurrency,
      formatDate,
      formatTimestamp,
      formatDecimal,
      getValue,
      formatStatus: (statusCode: string) => {
        const statusLookup = getValue(ENGAGEMENT_LOOKUP_CATEGORIES.STATUS, statusCode)
        return statusLookup?.value || statusCode
      },
      formatAction: (actionCode: string) => {
        return getActionLabel(actionCode as EngagementActionCode, dictionary) || actionCode
      },
    }
    return createEngagementAuditConfig(lookups)
  }, [getOrgName, getProfileName, getFieldMetadata, formatCurrency, formatDate, formatTimestamp, formatDecimal, getValue, dictionary])

  const isLoading = auditHistoryLoading || metadataLoading || lookupsLoading

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-0 z-50 bg-background",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "flex flex-col"
          )}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <History className="h-5 w-5 text-primary" />
              </div>
              <DialogPrimitive.Title className="text-lg font-semibold">
                Engagement {recordId}{engagementName ? ` - ${engagementName}` : ""} | Workflow History
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                View complete workflow history and changes for this engagement
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" className="rounded-sm">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </DialogPrimitive.Close>
          </div>

          {/* Modal Content (scrollable) */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {auditHistoryError || metadataError ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="rounded-full bg-destructive/10 p-3 mb-4">
                  <X className="h-6 w-6 text-destructive" />
                </div>
                <h3 className="text-lg font-medium mb-2">Failed to Load History</h3>
                <p className="text-sm text-muted-foreground max-w-md">
                  Unable to load audit history. Please try again later.
                </p>
              </div>
            ) : isLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                <p className="text-sm text-muted-foreground">Loading history...</p>
              </div>
            ) : (
              <HistoryContent
                currentVersion={currentVersion}
                config={auditConfig}
                validEntityIds={validEntityIds}
                getAuditByName={getAuditByName}
                auditHistory={auditHistory}
                resolveWorkerName={resolveWorkerName}
              />
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

